import React, { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Mail, Send, Calendar, RefreshCw, CheckCircle2, XCircle, Settings2, Loader2 } from 'lucide-react';

/** NotificationsPage
 *  ===============
 *  Admin-only dashboard for the email engine:
 *   – Delivery counters (sent / failed / skipped)
 *   – Log table with filter by event + status
 *   – Manual "send test" + "force daily digest" buttons
 *   – Config preview (sender, app URL, API key status)
 */
export default function NotificationsPage() {
  const { api } = useAuth();
  const [config, setConfig] = useState(null);
  const [logs, setLogs] = useState([]);
  const [counts, setCounts] = useState({});
  const [total, setTotal] = useState(0);
  const [event, setEvent] = useState('');
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [testBusy, setTestBusy] = useState(false);
  const [digestBusy, setDigestBusy] = useState(false);
  const [toast, setToast] = useState('');

  const [diagEmail, setDiagEmail] = useState('');
  const [diagBusy, setDiagBusy] = useState(false);
  const [diagResult, setDiagResult] = useState(null);

  const runDiag = async () => {
    if (!diagEmail) return;
    setDiagBusy(true); setDiagResult(null);
    try {
      const r = await api.get(`/notifications/diagnose?email=${encodeURIComponent(diagEmail.trim())}`);
      setDiagResult(r.data);
    } catch (e) {
      setDiagResult({ error: e?.response?.data?.detail || 'Diagnostic failed' });
    } finally {
      setDiagBusy(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const [c, l] = await Promise.all([
        api.get('/notifications/config'),
        api.get(`/notifications/logs?limit=200${event?`&event=${event}`:''}${status?`&status=${status}`:''}`),
      ]);
      setConfig(c.data);
      setLogs(l.data.rows || []);
      setCounts(l.data.counts || {});
      setTotal(l.data.total || 0);
    } catch (e) { /* ignore */ }
    setLoading(false);
  };

  useEffect(() => { load(); }, [event, status]);

  const sendTest = async () => {
    if (!testEmail) return;
    setTestBusy(true); setToast('');
    try {
      const r = await api.post('/notifications/test', { to: testEmail });
      setToast(r.data.provider_id ? `Sent — Resend id ${r.data.provider_id.slice(0,8)}` : 'Queued (no Resend key)');
      await load();
    } catch (e) { setToast(e.response?.data?.detail || 'Failed'); }
    setTestBusy(false);
  };

  const forceDigest = async () => {
    setDigestBusy(true); setToast('');
    try {
      const r = await api.post('/notifications/digest/now');
      setToast(r.data.sent ? `Digest sent to ${r.data.recipients} recipient(s)` : r.data.reason);
      await load();
    } catch (e) { setToast(e.response?.data?.detail || 'Failed'); }
    setDigestBusy(false);
  };

  const sent = counts.sent || 0;
  const failed = counts.failed || 0;
  const skipped = counts.skipped_no_key || 0;

  return (
    <div className="p-6 space-y-5" data-testid="notifications-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-white tracking-tight flex items-center gap-2">
            <Mail className="h-5 w-5 text-amber-400"/>
            Notifications
          </h1>
          <p className="text-[11px] text-zinc-500 mt-0.5">Resend-powered email delivery — every VawworldOS event routed to the right inbox</p>
        </div>
        <button onClick={load} className="h-9 w-9 rounded bg-zinc-900 border border-zinc-800 hover:border-amber-400 text-zinc-400 hover:text-amber-400 flex items-center justify-center" data-testid="notif-refresh">
          <RefreshCw className={`h-4 w-4 ${loading?'animate-spin':''}`}/>
        </button>
      </div>

      {/* Config + KPI */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div className="md:col-span-2 rounded-xl border border-zinc-800 bg-gradient-to-br from-amber-500/5 to-transparent p-4">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500">Sender identity</div>
          <div className="mt-1 flex items-center gap-2">
            <span className="text-lg font-bold text-white">{config?.sender || '—'}</span>
            {config?.resend_enabled
              ? <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-400 bg-emerald-500/10 border border-emerald-500/40 rounded px-1.5 py-0.5">LIVE</span>
              : <span className="text-[10px] font-bold uppercase tracking-wider text-orange-300 bg-orange-500/10 border border-orange-500/40 rounded px-1.5 py-0.5">NO KEY</span>}
          </div>
          <div className="mt-1 text-[11px] text-zinc-500">Branded on: <span className="text-zinc-300">{config?.app_url?.replace('https://','')}</span></div>
        </div>
        <Kpi label="Delivered" value={sent} Icon={CheckCircle2} color="emerald"/>
        <Kpi label="Failed / Skipped" value={failed + skipped} Icon={XCircle} color={failed>0?'rose':'zinc'}/>
      </div>

      {/* Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="md:col-span-2 rounded-xl border border-zinc-800 bg-zinc-950 p-4">
          <div className="flex items-center gap-2 text-zinc-300 text-sm font-semibold">
            <Send className="h-4 w-4 text-amber-400"/> Send a test email
          </div>
          <div className="mt-3 flex gap-2">
            <input
              value={testEmail}
              onChange={e=>setTestEmail(e.target.value)}
              placeholder="recipient@example.com"
              className="flex-1 bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm text-white focus:border-amber-400 outline-none"
              data-testid="notif-test-email"
            />
            <button onClick={sendTest} disabled={!testEmail || testBusy} className="px-4 py-2 rounded bg-amber-400 hover:bg-amber-300 disabled:opacity-40 text-zinc-900 text-xs font-bold flex items-center gap-1" data-testid="notif-test-btn">
              {testBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin"/> : <Send className="h-3.5 w-3.5"/>} Send
            </button>
          </div>
        </div>
        <div className="rounded-xl border border-zinc-800 bg-zinc-950 p-4 flex flex-col justify-between">
          <div>
            <div className="flex items-center gap-2 text-zinc-300 text-sm font-semibold">
              <Calendar className="h-4 w-4 text-violet-300"/> Daily Digest
            </div>
            <p className="mt-2 text-[11px] text-zinc-500">Sent automatically 9:00 AM IST to producers + admins.</p>
          </div>
          <button onClick={forceDigest} disabled={digestBusy} className="mt-3 px-3 py-2 rounded bg-violet-500/10 hover:bg-violet-500/20 border border-violet-500/40 text-violet-300 text-xs font-semibold flex items-center justify-center gap-1" data-testid="notif-digest-btn">
            {digestBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin"/> : <Send className="h-3.5 w-3.5"/>} Send now
          </button>
        </div>
      </div>

      {toast && <div className="text-[12px] text-amber-300 bg-amber-500/10 border border-amber-500/40 rounded px-3 py-2">{toast}</div>}

      {/* Diagnose a user's email pipeline — founder ask May 24, 2026:
          "Vendors are getting emails, freelancers are not." Type the
          freelancer's email and this walks the chain (user / WOs /
          email_logs) to pinpoint where the break is. */}
      <div className="rounded-xl border border-zinc-800 bg-zinc-950 p-4">
        <div className="flex items-center gap-2 text-zinc-300 text-sm font-semibold">
          <Settings2 className="h-4 w-4 text-cyan-400"/>
          Diagnose a user's email pipeline
        </div>
        <p className="mt-1 text-[11px] text-zinc-500">
          Enter any user's email (freelancer, vendor, employee) to trace exactly where their notifications are failing —
          user record · assigned work orders · email_logs · pause/toggle/Resend errors.
        </p>
        <div className="mt-3 flex gap-2">
          <input
            value={diagEmail}
            onChange={e=>setDiagEmail(e.target.value)}
            placeholder="freelancer@example.com"
            className="flex-1 bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm text-white focus:border-cyan-400 outline-none"
            data-testid="notif-diag-email"
          />
          <button onClick={runDiag} disabled={!diagEmail || diagBusy} className="px-4 py-2 rounded bg-cyan-400 hover:bg-cyan-300 disabled:opacity-40 text-zinc-900 text-xs font-bold flex items-center gap-1" data-testid="notif-diag-btn">
            {diagBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin"/> : <Settings2 className="h-3.5 w-3.5"/>} Diagnose
          </button>
        </div>
        {diagResult && !diagResult.error && (
          <div className="mt-3 space-y-2 text-[11px]" data-testid="notif-diag-result">
            {/* Summary chips — the headline finding */}
            <div className="flex flex-wrap gap-1.5">
              {diagResult.summary.map((s, i) => {
                const isOk = s.startsWith('DELIVERED_OK');
                const tone = isOk
                  ? 'bg-emerald-500/10 text-emerald-300 border-emerald-500/40'
                  : 'bg-rose-500/10 text-rose-200 border-rose-500/40';
                return (
                  <span key={i} className={`px-2 py-1 rounded border ${tone}`}>
                    {s}
                  </span>
                );
              })}
            </div>
            {/* User row */}
            {diagResult.user ? (
              <div className="bg-zinc-900/60 border border-zinc-800 rounded p-2">
                <span className="text-zinc-500 mr-2">User:</span>
                <span className="text-white font-mono">{diagResult.user.email}</span>
                <span className="text-zinc-500 mx-2">·</span>
                <span className="text-zinc-400">{diagResult.user.full_name}</span>
                <span className="text-zinc-500 mx-2">·</span>
                <span className="text-amber-300">{diagResult.user.role}</span>
                {diagResult.user.workforce_type && (
                  <>
                    <span className="text-zinc-500 mx-2">·</span>
                    <span className="text-violet-300">{diagResult.user.workforce_type}</span>
                  </>
                )}
              </div>
            ) : (
              <div className="text-rose-300">No user account found for this email.</div>
            )}
            {/* WO table */}
            {diagResult.work_orders?.length > 0 && (
              <div className="bg-zinc-900/60 border border-zinc-800 rounded">
                <div className="px-3 py-2 text-zinc-400 border-b border-zinc-800">
                  Recent Work Orders ({diagResult.work_orders.length})
                </div>
                <div className="divide-y divide-zinc-900">
                  {diagResult.work_orders.map((w) => (
                    <div key={w.work_order_no} className="px-3 py-1.5 flex flex-wrap gap-2 items-center">
                      <span className="font-mono text-cyan-300">{w.work_order_no}</span>
                      <span className="text-zinc-500">{w.status}</span>
                      <span className="text-zinc-500">→</span>
                      <span className={`font-mono ${w.assignee_email ? 'text-emerald-300' : 'text-rose-300'}`}>
                        {w.assignee_email || '(no email on WO)'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {/* email_logs */}
            <div className="bg-zinc-900/60 border border-zinc-800 rounded">
              <div className="px-3 py-2 text-zinc-400 border-b border-zinc-800">
                email_logs for this recipient ({diagResult.log_count})
              </div>
              {diagResult.email_logs?.length === 0 ? (
                <div className="px-3 py-2 text-zinc-500">No send attempts found.</div>
              ) : (
                <div className="divide-y divide-zinc-900 max-h-60 overflow-y-auto">
                  {diagResult.email_logs.map((r, i) => (
                    <div key={i} className="px-3 py-1.5 flex flex-wrap gap-2 items-start">
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${
                        r.status === 'sent' ? 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/40'
                        : r.status === 'failed' ? 'bg-rose-500/15 text-rose-300 border border-rose-500/40'
                        : 'bg-zinc-800 text-zinc-400 border border-zinc-700'
                      }`}>{r.status}</span>
                      <span className="text-zinc-400">{r.event}</span>
                      <span className="text-zinc-600 truncate flex-1">{r.subject}</span>
                      {r.error && <span className="text-rose-300 text-[10px] basis-full pl-3">↳ {r.error}</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
        {diagResult?.error && (
          <div className="mt-2 text-rose-300 text-[11px]">{diagResult.error}</div>
        )}
      </div>

      {/* Filters + log table */}
      <div className="rounded-xl border border-zinc-800 bg-zinc-950 overflow-hidden">
        <div className="flex items-center gap-2 border-b border-zinc-800 px-4 py-3 bg-zinc-900/50">
          <Settings2 className="h-3.5 w-3.5 text-zinc-500"/>
          <span className="text-xs text-zinc-400">Filter</span>
          <select value={event} onChange={e=>setEvent(e.target.value)} className="bg-zinc-900 border border-zinc-800 rounded text-xs text-white px-2 py-1" data-testid="filter-event">
            <option value="">All events</option>
            <option value="test">test</option>
            <option value="welcome">welcome</option>
            <option value="submission_created">submission_created</option>
            <option value="decision_internal_approved">internal approved</option>
            <option value="decision_internal_revise">internal revise</option>
            <option value="decision_client_approved">client approved</option>
            <option value="decision_client_revise">client revise</option>
            <option value="decision_client_rejected">client rejected</option>
            <option value="task_assigned">task_assigned</option>
            <option value="payment_released">payment_released</option>
            <option value="nda_pending">nda_pending</option>
            <option value="nda_signed">nda_signed</option>
            <option value="bot_alert">bot_alert</option>
            <option value="daily_digest">daily_digest</option>
          </select>
          <select value={status} onChange={e=>setStatus(e.target.value)} className="bg-zinc-900 border border-zinc-800 rounded text-xs text-white px-2 py-1" data-testid="filter-status">
            <option value="">All statuses</option>
            <option value="sent">sent</option>
            <option value="failed">failed</option>
            <option value="skipped_no_key">skipped</option>
          </select>
          <span className="flex-1"/>
          <span className="text-[11px] text-zinc-500">{total} total</span>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-zinc-900/40 text-[10px] uppercase tracking-wider text-zinc-500">
              <tr>
                <th className="text-left px-4 py-2 font-semibold">When</th>
                <th className="text-left px-4 py-2 font-semibold">Event</th>
                <th className="text-left px-4 py-2 font-semibold">Status</th>
                <th className="text-left px-4 py-2 font-semibold">Recipient</th>
                <th className="text-left px-4 py-2 font-semibold">Subject</th>
                <th className="text-left px-4 py-2 font-semibold">Detail</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((r,i) => (
                <tr key={i} className="border-t border-zinc-900 hover:bg-zinc-900/40" data-testid={`notif-log-row-${i}`}>
                  <td className="px-4 py-2 text-zinc-400 font-mono text-[11px] whitespace-nowrap">{r.sent_at?.slice(5,19).replace('T',' ')}</td>
                  <td className="px-4 py-2 text-zinc-300">{r.event}</td>
                  <td className="px-4 py-2">
                    <StatusTag s={r.status}/>
                  </td>
                  <td className="px-4 py-2 text-white">{r.recipient}</td>
                  <td className="px-4 py-2 text-zinc-400 max-w-[260px] truncate">{r.subject}</td>
                  <td className="px-4 py-2 text-[11px] text-zinc-500 max-w-[220px] truncate">{r.error || (r.provider_id ? `id ${r.provider_id.slice(0,8)}` : '')}</td>
                </tr>
              ))}
              {logs.length === 0 && !loading && (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-zinc-600 text-sm">No logs yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Kpi({ label, value, Icon, color = 'amber' }) {
  const palette = {
    amber:  'text-amber-300 border-amber-400/30 bg-amber-400/5',
    emerald:'text-emerald-300 border-emerald-400/30 bg-emerald-400/5',
    rose:   'text-rose-300 border-rose-400/30 bg-rose-400/5',
    zinc:   'text-zinc-300 border-zinc-700 bg-zinc-900/60',
  };
  return (
    <div className={`rounded-xl border px-4 py-3 ${palette[color]}`}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider opacity-80">
        <Icon className="h-3 w-3"/> {label}
      </div>
      <div className="text-2xl font-bold mt-0.5 text-white">{value}</div>
    </div>
  );
}

function StatusTag({ s }) {
  const map = {
    sent:           { c: 'text-emerald-300 bg-emerald-500/10 border-emerald-500/40', t: 'sent' },
    failed:         { c: 'text-rose-300 bg-rose-500/10 border-rose-500/40',          t: 'failed' },
    skipped_no_key: { c: 'text-orange-300 bg-orange-500/10 border-orange-500/40',    t: 'skipped' },
  };
  const m = map[s] || { c: 'text-zinc-300 bg-zinc-500/10 border-zinc-500/40', t: s };
  return <span className={`inline-block text-[10px] uppercase tracking-wider font-bold rounded px-1.5 py-0.5 border ${m.c}`}>{m.t}</span>;
}
